import { memo } from 'react'
import styled from 'styled-components'
import { type PromotionMetadata } from '@/features/shop/services/Product/types'
import {
  type ProductCardProduct,
  type OnComplementaryShelfFetch,
  type OnProductCallback,
  type OnProductClickCallback,
  type OnQtyCallback,
  type RenderQuickAdd,
} from '@/features/shop/components/ProductCard/types'
import { ProductCardImage } from '@/features/shop/components/ProductCard/ProductCardImage'
import { ProductCardPriceAndPromotion } from '@/features/shop/components/ProductCard/price/ProductCardPriceAndPromotion'
import { ProductCardName } from '@/features/shop/components/ProductCard/ProductCardName'
import { ProductCardQuickAdd } from '@/features/shop/components/ProductCard/ProductCardQuickAdd'
import { ProductVariationInfo } from '@/features/shop/components/ProductCard/colors/ProductVariationInfo'
import { BasicShadowHover } from '@/features/shared/styles/effects'
import { ProductCardSeeSimilarButton } from '@/features/shop/components/ProductCard/ProductCardSeeSimilarButton'
import { Column } from '@shipt/design-system-layouts'
import { type ShoppingListWithProduct } from '@/features/shop/services/ShoppingLists/types'
import { ProductCardFlag } from '@/features/shop/components/ProductCard/ProductCardFlag'
import { AddToListButton } from '@/features/shop/components/Lists/AddToListButton'
import { type UpdateProductQuantityFn } from '@/features/shared/hooks/useUpdateProductQuantity'
import { screenSizes } from '@shipt/design-system-themes'
import { ProductLink } from '@/features/shop/components/ProductCard/ProductLink'
import { MIN_HEIGHT } from '@shared/constants/productCard'
import dynamic from 'next/dynamic'
import { ScrollIntoView } from '@/features/shared/components/ScrollIntoView'
import { isGatedRoute } from '@shared/constants/accessLevel'
import { useCurrentRouteData } from '@/features/shared/hooks/routes/useCurrentRouteData'

const ProductCardUsuals = dynamic(
  () =>
    import('@/features/shop/components/ProductCard/ProductCardUsuals').then(
      (mod) => mod.ProductCardUsuals,
    ),
  { ssr: false },
)

type Props = {
  product: ProductCardProduct
  onClick: OnProductClickCallback
  onInView: OnProductCallback
  index: number
  showSeeSimilar?: boolean
  eagerLoad?: boolean
  usualsList?: ShoppingListWithProduct
  renderQuickAdd?: RenderQuickAdd
  showLargerProducts?: boolean
  showAddToListButton?: boolean
  isInSponsoredShelf?: boolean
  hideRatings?: boolean
  hideSecondaryDetails?: boolean
  linkToUniversalPDP: boolean
  onComplementaryShelfFetch?: OnComplementaryShelfFetch
  promotionMetadata?: PromotionMetadata | null
} & (
  | {
      renderQuickAdd: RenderQuickAdd
      productQuantity?: number
      updateProductQuantity?: UpdateProductQuantityFn
      onQtyAdded?: OnQtyCallback
      onQtyRemoved?: OnProductCallback
    }
  | {
      renderQuickAdd?: never
      productQuantity: number
      updateProductQuantity: UpdateProductQuantityFn
      onQtyAdded: OnQtyCallback
      onQtyRemoved: OnProductCallback
    }
)
export const ProductCard = memo(
  ({
    product,
    onQtyAdded,
    onQtyRemoved,
    onClick,
    onInView,
    index = 0,
    showSeeSimilar = false,
    eagerLoad = false,
    usualsList,
    renderQuickAdd,
    showLargerProducts = false,
    showAddToListButton = false,
    isInSponsoredShelf = false,
    productQuantity,
    updateProductQuantity,
    hideRatings,
    hideSecondaryDetails,
    onComplementaryShelfFetch,
    linkToUniversalPDP,
    promotionMetadata,
  }: Props) => {
    const { oos = false, parent } = product
    const routeData = useCurrentRouteData()

    const renderProductCardQuickAdd = () => {
      if (renderQuickAdd) return renderQuickAdd({ index, product })
      if (!isGatedRoute(routeData?.access) || oos) return null
      return (
        <ProductCardQuickAdd
          product={product}
          index={index}
          onQtyAdded={onQtyAdded}
          onQtyRemoved={onQtyRemoved}
          shouldShowPopover
          showProductFlag
          showLargerProducts={showLargerProducts}
          productQuantity={productQuantity}
          updateProductQuantity={updateProductQuantity}
          onComplementaryShelfFetch={onComplementaryShelfFetch}
          promotionMetadata={promotionMetadata}
        />
      )
    }

    return (
      <ProductCardScrollIntoView
        data-show-larger-products={showLargerProducts}
        onInView={() => onInView({ product, index })}
      >
        {!!oos && <ProductCardFlag product={product} />}
        <ProductCardLinkable
          product={product}
          onClick={onClick}
          index={index}
          linkToUniversalPDP={linkToUniversalPDP}
        >
          <ContentContainer>
            <ProductImage
              product={product}
              eagerLoad={eagerLoad}
              showLargerProducts={showLargerProducts}
            />
            <ProductCardContentWrapper>
              <ProductCardPriceAndPromotion
                product={product}
                hideSecondaryDetails={hideSecondaryDetails}
              />
              {!!parent && <ProductVariationInfo variationParent={parent} />}
              <ProductCardName
                product={product}
                isInSponsoredShelf={isInSponsoredShelf}
                hideRatings={hideRatings}
                hideSecondaryDetails={hideSecondaryDetails}
              />
            </ProductCardContentWrapper>
          </ContentContainer>
        </ProductCardLinkable>
        <Column align="start" spacing="md" padding={['xs', 0, 0, 0]}>
          {!!showSeeSimilar && <ProductCardSeeSimilarButton product={product} />}
          {!!usualsList && (
            <ProductCardUsuals product={product} index={index} usualsList={usualsList} />
          )}
          {!!showAddToListButton && <AddToListButton product={product} />}
        </Column>
        {renderProductCardQuickAdd()}
      </ProductCardScrollIntoView>
    )
  },
)

// DO NOT REMOVE - "Advanced" Usage of styled components.
// Parent calling a child's actions are only available this way.
// https://styled-components.com/docs/advanced#caveat
const ProductImage = styled(ProductCardImage)``

const ProductCardLinkable = styled(ProductLink)`
  display: block;
  height: 100%;
`
const ContentContainer = styled.div`
  height: 100%;
  display: flex;
  flex-direction: column;
  cursor: pointer;
`

const ProductCardScrollIntoView = styled(ScrollIntoView)<{
  'data-show-larger-products': boolean
}>`
  position: relative;
  max-width: 9.625rem;

  /* do not remove this min-height, it is needed in order for LazyLoadObserves
  IntersectionObserver to function properly and trigger the lazy load */
  min-height: ${MIN_HEIGHT}px;
  height: auto;
  width: auto;

  &:hover ${ProductImage} {
    ${BasicShadowHover};
    border-radius: 0.5rem;
  }

  @media ${screenSizes.tablet} {
    &[data-show-larger-products='true'] {
      max-width: 18rem;
    }
  }
`

const ProductCardContentWrapper = styled(Column)`
  overflow: hidden;
`
